import React, { useState, useEffect } from "react"
import { auth, db, storage } from "../../utils/firebase"
import firebase from "firebase"
import moment from "moment"
import "./document-uploads.scss"

// UI imports
import Tile from "../../components/structure/tile/tile"
import Title from "../../components/structure/title/title"
import Button from "../../components/ui/button/button"
import Table from "../../components/structure/table/table"
import Input from "../../components/ui/inputs/input"
import { TimesIcon } from "../../utils/svgs"
import { PlusIcon, TrashIcon } from "lucide-react"

// Returns the document uploads page
export default function DocumentUploads() {
	const [loading, setLoading] = useState(false)
	const [documents, setDocuments] = useState([])
	const [uploading, setUploading] = useState(false)
	const [uploadProgress, setUploadProgress] = useState(0)
	const [selectedFile, setSelectedFile] = useState(null)
	const [showUploadModal, setShowUploadModal] = useState(false)
	const [bookingReferences, setBookingReferences] = useState([])
	const [currentReference, setCurrentReference] = useState("")
	const [currentPage, setCurrentPage] = useState(1)
	const [itemsPerPage] = useState(20)
	const [documentTitle, setDocumentTitle] = useState("")
	const [checkingReference, setCheckingReference] = useState(false)

	// Load documents on component mount
	useEffect(() => {
		fetchDocuments()
	}, [])

	// Fetch all documents from Firestore
	const fetchDocuments = async () => {
		// Set the loading state to true
		setLoading(true)

		try {
			// Fetch all documents from the database
			const docs = await db.collection("invoice_documents").orderBy("created", "desc").get()

			const documentsData = await Promise.all(
				docs.docs.map(async (doc) => {
					// Get the data from the document
					const data = doc.data()

					// Fetch client names for each booking reference
					const bookingData = await Promise.all(
						data.connected_references.map(async (ref) => {
							try {
								let booking = await db.collection("bookings").where("reference", "==", ref).limit(1).get()

								// If not found, try parsing as integer
								if (booking.empty && !isNaN(Number(ref))) {
									booking = await db.collection("bookings").where("reference", "==", Number(ref)).limit(1).get()
								}

								if (!booking.empty) {
									const bookingData = booking.docs[0].data()

									// Get client data
									const client = await db.doc(`clients/${bookingData.client}`).get()

									return {
										reference: ref,
										client: client.exists ? `${client.data().first_name} ${client.data().last_name}` : "Unknown Client",
										bookingID: booking.docs[0].id,
									}
								}
								return { reference: ref, clientName: "Booking Not Found", bookingId: null }
							} catch (error) {
								console.error("Error fetching booking data:", error)
								return { reference: ref, clientName: "Error", bookingId: null }
							}
						})
					)

					return {
						id: doc.id,
						...data,
						bookingData,
					}
				})
			)

			setDocuments(documentsData)
		} catch (error) {
			console.error("Error fetching documents:", error)
		} finally {
			setLoading(false)
		}
	}

	// Handle file selection
	const handleFileSelect = (event) => {
		const file = event.target.files[0]
		if (file) {
			// Check if file is PDF or image
			const validTypes = ["application/pdf", "image/jpeg", "image/jpg", "image/png", "image/gif"]
			if (validTypes.includes(file.type)) {
				setSelectedFile(file)
			} else {
				alert("Please select a PDF or image file")
			}
		}
	}

	// Handle the uploading of the file into the storage bucket
	const handleFileUpload = async () => {
		if (!selectedFile) return

		setUploading(true)
		setUploadProgress(0)

		try {
			// Generate unique ID for the document
			const documentId = db.collection("invoice_documents").doc().id
			const fileName = `${documentId}_${selectedFile.name}`

			// Get the current year & month for the file path
			const year = moment().format("YYYY")
			const month = moment().format("MM")

			// Upload to Firebase Storage
			const uploadTask = storage.ref(`invoice_documents/${year}/${month}/${fileName}`).put(selectedFile)

			// Monitor upload progress
			uploadTask.on(
				"state_changed",
				(snapshot) => {
					const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
					setUploadProgress(progress)
				},
				(error) => {
					console.error("Upload error:", error)
					setUploading(false)
				},
				async () => {
					// Get download URL
					const downloadURL = await uploadTask.snapshot.ref.getDownloadURL()

					// Save document metadata to Firestore
					await db
						.collection("invoice_documents")
						.doc(documentId)
						.set({
							file_name: selectedFile.name,
							file_size: selectedFile.size,
							file_type: selectedFile.type,
							url: downloadURL,
							title: documentTitle.trim() || null,
							uploaded_by: auth.currentUser.uid,
							created: firebase.firestore.FieldValue.serverTimestamp(),
							connected_references: bookingReferences.map((ref) => ref.reference),
							full_path: `invoice_documents/${year}/${month}/${fileName}`,
						})

					// Reset form
					setSelectedFile(null)
					setBookingReferences([])
					setCurrentReference("")
					setDocumentTitle("")
					setShowUploadModal(false)
					setUploading(false)
					setUploadProgress(0)

					// Refresh documents list
					await fetchDocuments()
				}
			)
		} catch (error) {
			console.error("Error uploading file:", error)
			setUploading(false)
		}
	}

	// Add booking reference
	const addBookingReference = async () => {
		// If there is no reference, return
		if (!currentReference.trim()) return

		// Set the checking reference state to true
		setCheckingReference(true)

		try {
			// Trim the reference
			const referenceValue = currentReference.trim()

			// Set the booking to null
			let booking = null

			// First try to find the booking with the reference as-is
			booking = await db.collection("bookings").where("reference", "==", referenceValue).limit(1).get()

			// If not found, try parsing as integer
			if (booking.empty && !isNaN(Number(referenceValue))) {
				booking = await db.collection("bookings").where("reference", "==", Number(referenceValue)).limit(1).get()
			}

			// If not found, return null
			if (booking.empty) return

			// Get the booking document
			const bookingData = booking.docs[0].data()

			// Get client data
			const client = await db.doc(`clients/${bookingData.client}`).get()
			const clientName = client.exists ? `${client.data().first_name} ${client.data().last_name}` : "Unknown Client"

			// Check if reference already exists
			if (bookingReferences.some((ref) => ref.reference === referenceValue)) return

			// Add the booking reference to the state
			setBookingReferences([
				...bookingReferences,
				{
					reference: referenceValue,
					client: clientName,
					bookingId: booking.docs[0].id,
				},
			])

			// Reset the current reference
			setCurrentReference("")
		} catch (error) {
			console.error("Error adding booking reference:", error)
		} finally {
			setCheckingReference(false)
		}
	}

	// Remove booking reference
	const removeBookingReference = (index) => {
		setBookingReferences(bookingReferences.filter((_, i) => i !== index))
	}

	// Open document in a new tab
	const openDocument = (document) => {
		window.open(document.url, "_blank", "noopener,noreferrer")
	}

	// Delete document
	const deleteDocument = async (id, full_path) => {
		// Remove the file from the storage bucket
		await storage.ref(full_path).delete()

		// Remove the document from the database
		await db.collection("invoice_documents").doc(id).delete()

		// Refresh the documents
		fetchDocuments()
	}

	// Calculate pagination
	const totalPages = Math.ceil(documents.length / itemsPerPage)
	const startIndex = (currentPage - 1) * itemsPerPage
	const endIndex = startIndex + itemsPerPage
	const currentDocuments = documents.slice(startIndex, endIndex)

	return (
		<Tile fullPage={true}>
			<Title className="flex has-select-field">
				<h1>Document Uploads</h1>
				<Button
					onClick={() => setShowUploadModal(true)}
					className="is-primary"
					label="Upload Document"
				/>
			</Title>

			{/* Documents Table */}
			<Table
				className="documents-table"
				headings={["Document Name", "Upload Date", "Booking References", ""]}
				noResults={documents.length === 0}
				noResultsMessage={"No documents uploaded yet"}>
				{currentDocuments.map((document) => (
					<tr key={document.id}>
						<td>
							<a
								href={document.url}
								target="_blank"
								rel="noopener noreferrer"
								className="document-link">
								{document.title || document.file_name}
							</a>
						</td>
						<td>{moment(document.created.toDate()).format("DD/MM/YYYY HH:mm")}</td>
						<td className="bookings-cell">
							<div className="bookings-tooltip">
								{document.bookingData.map((booking, index) => (
									<div
										key={index}
										className="booking-reference">
										<a
											href={`/booking/${booking.bookingID}`}
											target="_blank"
											rel="noopener noreferrer">
											{booking.reference}
										</a>
										<span className="client-name"> - {booking.client}</span>
									</div>
								))}
							</div>
						</td>
						<td className="is-button flex">
							<Button
								small
								label="View"
								onClick={() => openDocument(document)}
							/>
							<Button
								small
								label="Delete"
								icon={<TrashIcon />}
								onClick={() => deleteDocument(document.id, document.full_path)}
							/>
						</td>
					</tr>
				))}
			</Table>

			{/* Pagination */}
			{totalPages > 1 && (
				<div className="pagination">
					<Button
						onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
						disabled={currentPage === 1}
						className="is-secondary"
						label="Previous"
					/>
					<span className="pagination-info">
						Page {currentPage} of {totalPages}
					</span>
					<Button
						onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
						disabled={currentPage === totalPages}
						className="is-secondary"
						label="Next"
					/>
				</div>
			)}

			{/* Upload Modal */}
			{showUploadModal && (
				<div
					className="modal-overlay"
					onClick={() => setShowUploadModal(false)}>
					<div
						className="modal-content"
						onClick={(e) => e.stopPropagation()}>
						<div className="modal-header">
							<h2>Upload Document</h2>
							<Button
								xsSmall
								icon={<TimesIcon />}
								onClick={() => setShowUploadModal(false)}
							/>
						</div>
						<div className="upload-form">
							<div className="title-input-section">
								<Input
									placeholder="Document title (optional)"
									value={documentTitle}
									onChange={setDocumentTitle}
									label="Document Title"
									showLabel={true}
								/>
							</div>
							<div className="file-input-section">
								<label
									htmlFor="file-input"
									className="file-input-label">
									Choose File (PDF or Image)
								</label>
								<input
									id="file-input"
									type="file"
									accept=".pdf,.jpg,.jpeg,.png,.gif"
									onChange={handleFileSelect}
									className="file-input"
								/>
								{selectedFile && (
									<div className="selected-file">
										Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
									</div>
								)}
							</div>

							{uploading && (
								<div className="upload-progress">
									<div className="progress-bar">
										<div
											className="progress-fill"
											style={{ width: `${uploadProgress}%` }}></div>
									</div>
									<span>Uploading... {uploadProgress}%</span>
								</div>
							)}

							<div className="booking-references-section">
								<h3>Booking References</h3>
								<div className="reference-input">
									<Input
										placeholder="Enter booking reference"
										value={currentReference}
										onChange={setCurrentReference}
										onKeyPress={(e) => e.key === "Enter" && addBookingReference()}
									/>
									<Button
										onClick={addBookingReference}
										disabled={!currentReference.trim() || checkingReference}
										loading={checkingReference}
										loadingText="Checking..."
										icon={<PlusIcon />}
										label="Add"
									/>
								</div>

								{bookingReferences.length > 0 && (
									<div className="references-list">
										{bookingReferences.map((ref, index) => (
											<div
												key={index}
												className="reference-item">
												<span className="reference-text">
													{ref.reference} - {ref.client}
												</span>
												<button
													onClick={() => removeBookingReference(index)}
													className="remove-reference">
													×
												</button>
											</div>
										))}
									</div>
								)}
							</div>

							<div className="modal-actions">
								<Button
									onClick={() => setShowUploadModal(false)}
									className="is-secondary"
									label="Cancel"
								/>
								<Button
									onClick={handleFileUpload}
									disabled={!selectedFile || uploading}
									className="is-primary"
									label={uploading ? "Uploading..." : "Upload Document"}
								/>
							</div>
						</div>
					</div>
				</div>
			)}
		</Tile>
	)
}
